/**
 * Today's social drafts, approved from the dashboard.
 *
 * THE ONE HARD RULE HERE: publishing is irreversible and there is no undo. A misfired
 * click puts copy on a real account with real followers, and deleting it afterwards is not
 * the same as never having posted. So approval is two presses, and the second press names
 * the account it is about to post to. That is not friction for its own sake, it is the
 * only affordance that makes the account visible at the moment of committing.
 *
 * The button also states WHEN. Postiz's public API has no publish-now transition, so
 * approving hands the post to its scheduler: that is immediate when the post's time has
 * passed and later when it has not. The label says which, recomputed on a timer, because a
 * string baked at fetch time would still claim "in 2 minutes" five minutes later. Saying
 * "publishing now" for something timed for 09:56 would be a lie he could only discover by
 * watching the account.
 *
 * Nothing here is a card grid. One draft is one row: the copy is the content, and wrapping
 * each in a bordered box would make nine accounts look like nine products.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  approveSocialDraft,
  fetchSocialToday,
  holdSocialPost,
  whenLabel2,
  type Loaded,
  type SocialDraft,
  type SocialToday,
} from './api.js';
import { Icon, type IconName } from './Icon.js';

/** Platform slug to icon. Unknown platforms get the generic one rather than nothing. */
function platformIcon(platform: string): IconName {
  // Mapped onto the icons that exist rather than inventing names: `Icon` is a closed set
  // and a missing glyph renders as nothing at all, which reads as a broken row.
  if (platform.startsWith('linkedin') || platform === 'facebook') return 'project';
  if (platform.startsWith('instagram')) return 'update';
  if (platform.startsWith('youtube') || platform === 'tiktok') return 'clock';
  return 'notes';
}

export function SocialPanel(): JSX.Element | null {
  const [data, setData] = useState<Loaded<SocialToday>>({ state: 'loading' });
  /** Which draft is one press from going out. Only ever one at a time. */
  const [arming, setArming] = useState('');
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');
  /** Ticks so the "immediately / in 20 minutes" labels stay true while he reads. */
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    setData(await fetchSocialToday());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Arming is abandoned if he looks away long enough for the timing label to have changed
  // meaning. A primed publish button left sitting is exactly the accident to avoid.
  useEffect(() => {
    if (arming === '') return;
    const t = setTimeout(() => setArming(''), 15_000);
    return () => clearTimeout(t);
  }, [arming]);

  if (data.state === 'loading') {
    return (
      <section className="deck-panel" data-hue="intent">
        <Head count={undefined} />
        <p className="mo-empty">Checking today&apos;s queue…</p>
      </section>
    );
  }

  // Not configured is a sentence, not an empty queue. An empty box here would read as
  // "nothing to post today", which is a different and wrong claim.
  if (data.state === 'disabled' || data.state === 'error') {
    return (
      <section className="deck-panel" data-hue="intent">
        <Head count={undefined} />
        <p className="deck-panel-note">{data.message}</p>
      </section>
    );
  }

  const { drafts, scheduled, published, failed, problems } = data.value;

  const act = async (d: SocialDraft, how: 'approve' | 'hold'): Promise<void> => {
    setBusy(d.id);
    setProblem('');
    const r = how === 'approve' ? await approveSocialDraft(d.id) : await holdSocialPost(d.id);
    setBusy('');
    setArming('');
    if (r.ok) await load();
    else setProblem(r.error);
  };

  return (
    <section className="deck-panel mo-social" data-hue="intent">
      {/* Both actionable buckets: a count of drafts alone would read as 0 on a normal day
          while three posts were minutes from going out. */}
      <Head count={drafts.length + scheduled.length} />

      {problems.map((p) => (
        <p key={p} className="deck-panel-note" role="status">
          {p}
        </p>
      ))}

      {/*
        WHAT APPROVING ACTUALLY DOES RIGHT NOW, and it is not what the word implies.
        Investigated 2026-08-05 (docs/findings/2026-08-05-social-pipeline.md): Postiz's
        scheduler runs on Temporal and `temporal workflow list` returns zero workflows, so a
        post in QUEUE has nothing scheduled to publish it. Nothing has published since 23 July
        despite the daily Hermes job queueing every morning.
        The panel therefore says so rather than letting the button imply a publish it cannot
        cause. Removing this line is only correct once Postiz is publishing again.
      */}
      {(scheduled.length > 0 || drafts.length > 0) && (
        <p className="mo-social-note">
          Postiz has not published since 23 July: its scheduler has no queued work, so
          approving marks a post ready without sending it. Holding still prevents a future
          send.
        </p>
      )}
      {problem !== '' && <p className="mo-social-problem">{problem}</p>}

      {drafts.length === 0 && scheduled.length === 0 && problems.length === 0 && (
        <p className="mo-empty">
          Nothing is queued or waiting today.
          {published.length > 0 && ` ${published.length} already went out.`}
        </p>
      )}

      {/*
        SCHEDULED FIRST, and this ordering came from the real queue rather than a guess. His
        pipeline files posts straight to QUEUE, so on a normal day the drafts list is empty
        and everything of interest is already on its way out. Burying that under an empty
        "waiting for approval" heading would hide the only thing worth seeing.
      */}
      {scheduled.length > 0 && (
        <>
          <p className="mo-social-section">
            Going out today
            <span className="mo-social-count">{scheduled.length}</span>
          </p>
          <ul className="mo-social-list">
            {scheduled.map((d) => (
              <Row
                key={d.id}
                d={d}
                busy={busy === d.id}
                // Holding cannot post anything, so it needs no confirm step. The
                // two-press guard is reserved for the direction that is irreversible.
                action={{ label: 'Hold', kind: 'hold', busyLabel: 'Holding…' }}
                onAct={() => void act(d, 'hold')}
              />
            ))}
          </ul>
        </>
      )}

      {drafts.length > 0 && (
        <>
          <p className="mo-social-section">
            Waiting on you
            <span className="mo-social-count">{drafts.length}</span>
          </p>
          <ul className="mo-social-list">
            {drafts.map((d) => {
              const when = whenLabel2(d.publishAt);
              const armed = arming === d.id;
              return (
                <Row
                  key={d.id}
                  d={d}
                  busy={busy === d.id}
                  armed={armed}
                  action={{
                    label: armed ? `Post to ${d.account} ${when}` : 'Approve',
                    kind: armed ? 'confirm' : 'approve',
                    busyLabel: 'Posting…',
                  }}
                  onAct={() => (armed ? void act(d, 'approve') : setArming(d.id))}
                  onCancel={() => setArming('')}
                />
              );
            })}
          </ul>
        </>
      )}

      {failed.length > 0 && (
        <p className="mo-social-problem">
          {failed.length} post{failed.length === 1 ? '' : 's'} failed to publish. Check Postiz.
        </p>
      )}

      {published.length > 0 && (drafts.length > 0 || scheduled.length > 0) && (
        <p className="deck-panel-note">{published.length} already went out today.</p>
      )}
    </section>
  );
}

/**
 * One post. Deliberately not a card: the copy is the content, and a bordered box per post
 * would turn a handful of rows into a product grid.
 */
function Row({
  d,
  busy,
  armed,
  action,
  onAct,
  onCancel,
}: {
  d: SocialDraft;
  busy: boolean;
  armed?: boolean;
  action: { label: string; kind: 'approve' | 'confirm' | 'hold'; busyLabel: string };
  onAct: () => void;
  onCancel?: () => void;
}): JSX.Element {
  const when = whenLabel2(d.publishAt);
  return (
    <li className="mo-social-item">
      <p className="mo-social-meta">
        <Icon name={platformIcon(d.platform)} size={13} />
        <span className="mo-social-account">{d.account}</span>
        <span className="mo-social-when">{when === 'immediately' ? 'due now' : when}</span>
      </p>

      <p className="mo-social-text">{d.text === '' ? '(no copy)' : d.text}</p>

      {d.image !== null && (
        // Lazy, and height capped by CSS rather than by the image: a tall render would push
        // the rest of the queue out of the panel.
        <img className="mo-social-image" src={d.image} alt="" loading="lazy" />
      )}

      <p className="mo-social-actions">
        <button
          type="button"
          className={
            action.kind === 'confirm'
              ? 'mo-btn mo-btn-danger'
              : action.kind === 'approve'
                ? 'mo-btn mo-btn-primary'
                : 'mo-btn'
          }
          disabled={busy}
          aria-label={
            action.kind === 'confirm'
              ? `Confirm: post to ${d.account} ${when}. This cannot be undone.`
              : action.kind === 'hold'
                ? `Hold the post to ${d.account} so it does not go out`
                : `Approve the post to ${d.account}`
          }
          onClick={onAct}
        >
          {busy ? action.busyLabel : action.label}
        </button>
        {armed === true && onCancel !== undefined && (
          <button type="button" className="mo-btn" onClick={onCancel}>
            Cancel
          </button>
        )}
        {armed === true && <span className="mo-social-warn">No undo.</span>}
      </p>
    </li>
  );
}

function Head({ count }: { count: number | undefined }): JSX.Element {
  return (
    <header className="deck-panel-head">
      <Icon name="update" size={16} className="deck-panel-icon" />
      <h2 className="deck-panel-title">Posts to approve</h2>
      {count !== undefined && <span className="deck-count">{count}</span>}
    </header>
  );
}
